<template>
    <base-form-section class="relative">
        <p class="mat16">
            {{
                say.two_factor_auth_description ||
                'Zabezpiecz konto administratora kodem z aplikacji Google Authenticator.'
            }}
        </p>

        <p class="mat16">
            <b v-if="enabled">
                {{ say.two_factor_enabled || '2FA jest włączone' }}
            </b>
            <b v-else>
                {{ say.two_factor_disabled || '2FA jest wyłączone' }}
            </b>
        </p>

        <base-notification-local v-if="enabled" negative class="two-factor-warning">
            <template v-slot:header>
                {{ say.two_factor_logout_warning_title || 'Uwaga — ryzyko utraty dostępu' }}
            </template>
            <template v-slot:content>
                {{
                    say.two_factor_logout_warning ||
                    'Jeśli wylogujesz się bez wcześniejszego połączenia konta z Google Authenticator (skan QR lub wpisanie sekretu), stracisz dostęp do konta administratora.'
                }}
            </template>
        </base-notification-local>

        <p v-if="actionError" class="error error--show mat16">
            {{ actionError }}
        </p>

        <div v-if="enabled && (secret || qrObjectUrl)" class="two-factor-setup mat24">
            <p class="mat16">
                {{
                    say.two_factor_setup_warning ||
                    'Zapisz sekret lub zeskanuj QR teraz. Przy następnym logowaniu będzie wymagany kod z aplikacji.'
                }}
            </p>

            <p v-if="secret" class="mat16">
                <strong>{{ say.two_factor_secret || 'Sekret (wpis ręczny)' }}:</strong>
                <code class="two-factor-setup__secret">{{ secret }}</code>
            </p>

            <div v-if="qrObjectUrl" class="mat16">
                <p>{{ say.two_factor_scan_qr || 'Zeskanuj kod QR w aplikacji Google Authenticator' }}</p>
                <img class="two-factor-setup__qr" :src="qrObjectUrl" alt="QR code 2FA" />
            </div>
        </div>

        <div class="row mat24">
            <div class="col-12 col-md-4">
                <base-button v-if="!enabled" btn1 :disabled="loading" @click="enable2FA()">
                    {{ say.enable_2fa || 'Włącz 2FA' }}
                </base-button>
                <base-button v-else btn6 :disabled="loading" @click="disable2FA()">
                    {{ say.disable_2fa || 'Wyłącz 2FA' }}
                </base-button>
            </div>
        </div>
    </base-form-section>
</template>

<script>
    import { mapGetters } from 'vuex';
    import api from '@/api';

    export default {
        data() {
            return {
                enabled: false,
                secret: null,
                qrObjectUrl: null,
                loading: false,
                actionError: null,
            };
        },
        computed: {
            ...mapGetters(['say']),
        },
        async created() {
            await this.loadStatus();
        },
        beforeDestroy() {
            this.revokeQrUrl();
        },
        methods: {
            revokeQrUrl() {
                if (this.qrObjectUrl) {
                    window.URL.revokeObjectURL(this.qrObjectUrl);
                    this.qrObjectUrl = null;
                }
            },
            clearSetup() {
                this.secret = null;
                this.revokeQrUrl();
            },
            async loadStatus() {
                this.loading = true;
                this.actionError = null;
                try {
                    const status = await api.get('/admin/2fa/status');
                    this.enabled = Boolean(status.enabled);
                    if (this.enabled) {
                        await this.verifyAndShowSetup();
                    } else {
                        this.clearSetup();
                    }
                } catch (e) {
                    this.actionError = e?.message || e?.error || 'Nie udało się pobrać statusu 2FA';
                } finally {
                    this.loading = false;
                }
            },
            async verifyAndShowSetup() {
                try {
                    const data = await api.post('/admin/2fa/verify');
                    this.secret = data.secret || null;
                    await this.loadQrCode();
                } catch (e) {
                    // Configured flag may still be true; QR/secret optional on re-entry.
                    this.actionError = e?.message || e?.error || null;
                }
            },
            async loadQrCode() {
                this.revokeQrUrl();
                try {
                    const blob = await api.get('/admin/2fa/qrcode', {
                        responseType: 'blob',
                    });
                    const imageBlob = blob instanceof Blob ? blob : new Blob([blob], { type: 'image/png' });
                    this.qrObjectUrl = window.URL.createObjectURL(imageBlob);
                } catch (e) {
                    // QR optional if endpoint fails; secret may still be shown.
                }
            },
            async enable2FA() {
                this.loading = true;
                this.actionError = null;
                try {
                    const data = await api.post('/admin/2fa/enable');
                    this.enabled = true;
                    this.secret = data.secret || null;
                    await this.loadQrCode();
                } catch (e) {
                    this.actionError = e?.message || e?.error || 'Nie udało się włączyć 2FA';
                } finally {
                    this.loading = false;
                }
            },
            async disable2FA() {
                this.loading = true;
                this.actionError = null;
                try {
                    await api.post('/admin/2fa/disable');
                    this.enabled = false;
                    this.clearSetup();
                } catch (e) {
                    this.actionError = e?.message || e?.error || 'Nie udało się wyłączyć 2FA';
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>

<style scoped>
    .two-factor-warning {
        margin-top: 16px;
        margin-bottom: 8px;
        padding: 20px 16px;
    }
    .two-factor-warning ::v-deep .notification__header {
        font-size: 1.15rem;
        font-weight: 700;
    }
    .two-factor-warning ::v-deep .notification__content {
        font-size: 1rem;
        line-height: 1.45;
        margin-top: 12px;
    }
    .two-factor-setup__secret {
        display: inline-block;
        margin-left: 8px;
        letter-spacing: 0.05em;
        word-break: break-all;
    }
    .two-factor-setup__qr {
        display: block;
        max-width: 220px;
        height: auto;
        margin-top: 8px;
    }
    .error {
        color: var(--color-pinky-red, #ff0000);
        font-size: 14px;
        font-weight: 500;
        line-height: 1.45;
        margin-top: 16px;
    }
</style>
